// useMarketingTags.ts — CORE-1142
//
// Подключение внешнего маркетингового слоя к жизни приложения: согласие и
// регион. Сам слой (`marketingTagRuntime`) ничего не знает ни про React, ни про
// то, откуда берутся эти два ответа.

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { marketingTags } from '../services/marketingTagRuntime';
import { loadCookieConsent, peekCookieConsent, subscribeCookieConsent } from '../services/cookieConsent';
import { effectiveCookieChoice } from '../services/consentLogic';
import { getUserRegion } from '../services/region';
import { countryFromTimezone } from '../utils/restrictedRegion';

/**
 * Определить страну для маршрутизации счётчиков.
 *
 * Правило намеренно НЕсимметрично, и это главное решение этого файла.
 *
 * GeoIP — единственный источник, которому мы верим в обе стороны. Когда он
 * молчит (не ответил, нет базы, режется сетью), в дело идёт таймзона — но
 * только в одну сторону: «похоже на Россию» принимается, «не похоже на Россию»
 * не принимается никогда. Таймзона ошибается ровно на экспатах — человек с
 * ноутбуком в Europe/Moscow сидит в Берлине, и наоборот, — и цена двух ошибок
 * разная. Принять россиянина за иностранца значит загрузить ему Google
 * Analytics: ровно тот вопрос, который на встрече 11.08 задали и оставили
 * открытым. Принять иностранца за россиянина значит показать ему Метрику —
 * неточность отчёта, не более.
 *
 * Поэтому «не знаю» остаётся «не знаю», и в этом состоянии не грузится ничего,
 * кроме площадок, положенных всем регионам: им ответ не нужен (CORE-1853).
 */
async function resolveCountry(): Promise<string | null> {
  const geo = await getUserRegion().catch(() => null);
  if (geo) return geo;
  return countryFromTimezone(
    (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
      } catch {
        return null;
      }
    })(),
  );
}

/**
 * Держит рантайм в курсе двух вещей: дал ли человек согласие и в каком он
 * регионе. Всё остальное — окружение, конфиг, дедупликацию — рантайм решает сам.
 */
export function useMarketingTags(ageGroup?: 'adult' | 'teen' | null): void {
  // CORE-1404 — 14–17 лет: счётчики не грузятся независимо от выбора по cookie.
  // Отдельный эффект, чтобы вход подростка сразу снимал подписку без повторного
  // чтения стораджа и региона.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!marketingTags.isEligible()) return;
    const consent = peekCookieConsent();
    if (consent === undefined) return;
    marketingTags.setConsent(effectiveCookieChoice(consent, ageGroup));
  }, [ageGroup]);

  const ageGroupRef = useRef(ageGroup);
  ageGroupRef.current = ageGroup;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Ни одного сетевого запроса и ни одного чтения стораджа, когда слой не
    // настроен: выключенная интеграция не должна стоить ничего.
    if (!marketingTags.isEligible()) return;

    let cancelled = false;

    void loadCookieConsent().then((consent) => {
      if (!cancelled) marketingTags.setConsent(effectiveCookieChoice(consent, ageGroupRef.current));
    });
    const unsubscribe = subscribeCookieConsent((consent) => {
      marketingTags.setConsent(effectiveCookieChoice(consent, ageGroupRef.current));
    });

    void resolveCountry().then((country) => {
      if (!cancelled) marketingTags.setCountry(country);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);
}
